import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  Loader2,
  Mail,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Switch } from "@/components/ui/Switch";
import { useAuth } from "@/contexts/AuthContext";
import {
  integrationsApi,
  ApiError,
  type ResendIntegrationStatus,
  type ResendSetupState,
  type ResendSetupStep,
} from "@/lib/api";

// TenantConfig — vendor integration management. Top-level sidebar route at
// /config. The first (and currently only) vendor is Resend; future entries
// (GitHub for the engineer agents, Slack for the EA, etc.) drop in as
// additional cards on this same page.
export function TenantConfig({ tenantCode }: { tenantCode: string }) {
  return (
    <div className="p-8 space-y-6">
      <header>
        <h1 className="font-display text-4xl">Config</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Connect external vendors that your agents call out to —
          transactional email, ticketing, source control, and so on. One
          connection per provider, scoped to this subdomain.
        </p>
      </header>
      <ResendCard tenantCode={tenantCode} />
    </div>
  );
}

function ResendCard({ tenantCode }: { tenantCode: string }) {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  const status = useQuery<ResendIntegrationStatus, ApiError>({
    queryKey: ["integration-resend", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      const res = await integrationsApi.getResend(tenantCode, idToken);
      return res.integration;
    },
    enabled: !!idToken,
  });

  // Wizard state — one source of truth for the stepped UI. setup is the
  // most recent state snapshot from the server (either inline from the POST
  // response or via the polling GET); awaitingVerify flags that the user
  // has clicked Get Key and the wizard is mid-flight.
  const [setup, setSetup] = useState<ResendSetupState | null>(null);
  const [awaitingVerify, setAwaitingVerify] = useState(false);

  const create = useMutation<
    { integration?: ResendIntegrationStatus; setup?: ResendSetupState; awaitingVerify: boolean },
    ApiError,
    void
  >({
    mutationFn: async () => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      const res = await integrationsApi.createResend(tenantCode, {}, idToken);
      return {
        integration: res.integration,
        setup: res.setup,
        awaitingVerify: !!res.awaiting_verify,
      };
    },
    onSuccess: (next) => {
      if (next.setup) setSetup(next.setup);
      setAwaitingVerify(next.awaitingVerify);
      if (next.integration) {
        queryClient.setQueryData(["integration-resend", tenantCode, idToken], next.integration);
        toast.success("Resend key minted — outbound email is live.");
      } else if (next.awaitingVerify) {
        toast.message("DNS records published — waiting for Resend to verify the subdomain.");
      }
    },
    onError: (err) => {
      console.error("create resend", err);
      if (err.code === "resend_admin_unavailable") {
        toast.error("Platform Resend admin token isn't configured", {
          description: "Reach out to support to enable this integration.",
        });
      } else if (err.code === "resend_setup_failed") {
        toast.error("Resend setup hit an error", { description: err.message });
      } else {
        toast.error("Couldn't connect Resend", { description: err.message });
      }
    },
  });

  // Poll the setup endpoint while the wizard is mid-flight. Stops the moment
  // OverallStatus flips to "verified" — at that point we re-call POST to
  // mint the key (the same handler returns the connected integration).
  useEffect(() => {
    if (!awaitingVerify || !idToken) return;
    let cancelled = false;
    let mounted = true;
    const tick = async () => {
      try {
        const res = await integrationsApi.getResendSetup(tenantCode, idToken);
        if (cancelled) return;
        setSetup(res.setup);
        if (res.setup.overall_status === "verified") {
          if (mounted) create.mutate();
        }
      } catch (err) {
        console.error("setup poll", err);
      }
    };
    const id = window.setInterval(tick, 5_000);
    tick();
    return () => {
      cancelled = true;
      mounted = false;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingVerify, idToken, tenantCode]);

  const remove = useMutation<{ disconnected: true }, ApiError, void>({
    mutationFn: async () => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      return integrationsApi.deleteResend(tenantCode, idToken);
    },
    onSuccess: () => {
      queryClient.setQueryData<ResendIntegrationStatus>(
        ["integration-resend", tenantCode, idToken],
        { connected: false, send_via_resend: false },
      );
      setSetup(null);
      setAwaitingVerify(false);
      toast.success("Resend disconnected");
    },
    onError: (err) => toast.error("Couldn't disconnect", { description: err.message }),
  });

  const patch = useMutation<ResendIntegrationStatus, ApiError, boolean>({
    mutationFn: async (next) => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      const res = await integrationsApi.patchResend(
        tenantCode,
        { send_via_resend: next },
        idToken,
      );
      return res.integration;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(["integration-resend", tenantCode, idToken], next);
    },
    onError: (err) => toast.error("Couldn't update toggle", { description: err.message }),
  });

  const data = status.data;
  const connected = !!data?.connected;
  const sendViaResend = !!data?.send_via_resend;
  const showWizard = !!setup && (awaitingVerify || (!connected && setup.overall_status !== "verified"));

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet shrink-0">
              <Mail className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium">Resend</p>
                <a
                  href="https://resend.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                >
                  resend.com <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Outbound transactional email. The CS agent uses this to reply
                to ticket reporters with a sender address tied to your domain.
                One Resend key per subdomain — delete and re-mint to rotate.
              </p>
            </div>
            {connected ? (
              <Button
                variant="outline"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                className="text-signal-failed hover:text-signal-failed hover:bg-signal-failed/10"
              >
                {remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete Key
              </Button>
            ) : (
              <Button onClick={() => create.mutate()} disabled={create.isPending || !idToken}>
                {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Get Key
              </Button>
            )}
          </div>

          <div className="rounded-md border border-border bg-muted/20 p-3 text-xs space-y-1">
            <div className="text-muted-foreground">
              Status:{" "}
              <span className={"font-mono " + (connected ? "text-signal-ok" : "text-foreground")}>
                {status.isLoading ? "checking…" : connected ? "connected" : "not connected"}
              </span>
            </div>
            {connected && data?.key_prefix ? (
              <div className="text-muted-foreground">
                Key prefix:{" "}
                <span className="font-mono text-foreground">{data.key_prefix}…</span>
              </div>
            ) : null}
            {connected && data?.name ? (
              <div className="text-muted-foreground">
                Resend label:{" "}
                <span className="font-mono text-foreground">{data.name}</span>
              </div>
            ) : null}
          </div>

          {showWizard && setup ? <ResendSetupWizard setup={setup} polling={awaitingVerify} /> : null}

          <div
            className={
              "flex items-start gap-3 rounded-md border border-border p-3 text-xs " +
              (connected ? "" : "opacity-60")
            }
          >
            <Switch
              checked={sendViaResend}
              onCheckedChange={(next) => patch.mutate(next)}
              disabled={!connected || patch.isPending}
              aria-label="Send transactional email via Resend"
            />
            <div className="flex-1">
              <p className="font-medium text-foreground">
                Send transactional email via Resend
              </p>
              <p className="mt-0.5 text-muted-foreground">
                When off, outbound stays on the platform default sender.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ResendSetupWizard — stepped status panel that mirrors the backend's
// integrations/resend.Setup flow. Steps render in fixed order (Register
// domain → Publish DNS → Verify) with a status icon per row. While the
// `polling` flag is true the parent is hitting GET /integrations/resend/setup
// every few seconds, so the user sees the verify row flip from in_progress
// to done without clicking anything. DNS records are surfaced in detail
// when the publish step fails so a tenant can debug zone-level conflicts.
function ResendSetupWizard({ setup, polling }: { setup: ResendSetupState; polling: boolean }) {
  return (
    <div className="rounded-md border border-brand-violet/30 bg-brand-violet/5 p-4 text-xs space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">Resend Setup Wizard</p>
        <span className="font-mono text-[11px] text-muted-foreground">{setup.hostname}</span>
      </div>
      <ol className="space-y-2">
        {setup.steps.map((step) => (
          <SetupStepRow key={step.name} step={step} />
        ))}
      </ol>
      {polling ? (
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Polling Resend every 5 seconds — DNS propagation typically takes 30–90 seconds.
        </p>
      ) : null}
      {setup.records && setup.records.length > 0 ? (
        <details className="rounded-md border border-border/40 bg-muted/30 p-2">
          <summary className="cursor-pointer text-[11px] text-muted-foreground">
            DNS records published ({setup.records.length})
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap font-mono text-[10px] text-foreground">
{setup.records.map((r) => `${r.type}  ${r.name}  ${r.value}`).join("\n")}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

function SetupStepRow({ step }: { step: ResendSetupStep }) {
  const labels: Record<ResendSetupStep["name"], string> = {
    domain_registered: "Register subdomain in Resend",
    dns_records_published: "Publish DKIM / SPF / MX records",
    verified: "Resend verifies DNS",
  };
  const Icon =
    step.status === "done"
      ? CheckCircle2
      : step.status === "in_progress"
      ? Loader2
      : step.status === "failed"
      ? XCircle
      : Circle;
  const colorClass =
    step.status === "done"
      ? "text-signal-ok"
      : step.status === "in_progress"
      ? "text-brand-violet"
      : step.status === "failed"
      ? "text-signal-failed"
      : "text-muted-foreground";
  return (
    <li className="flex items-start gap-2">
      <Icon className={`h-4 w-4 shrink-0 ${colorClass} ${step.status === "in_progress" ? "animate-spin" : ""}`} />
      <div className="flex-1 min-w-0">
        <p className="text-foreground">{labels[step.name]}</p>
        {step.detail ? (
          <p className={"mt-0.5 " + (step.status === "failed" ? "text-signal-failed" : "text-muted-foreground")}>
            {step.detail}
          </p>
        ) : null}
      </div>
    </li>
  );
}
